import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, PauseCircle, PlayCircle, MailX, LifeBuoy, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import client from "@/lib/api";

export default function Preferences() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setError("Missing preferences link. Please open the email again."); return; }
    client.get(`/preferences/inspect?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is no longer valid."));
  }, [token]);

  const run = async (action) => {
    setBusy(true);
    try {
      const r = await client.post(`/preferences/update`, { token, action });
      setDone({ action, message: `Your preference has been updated to ${r.data.emailDeliveryStatus || action}.` });
    } catch (e) {
      setError(e?.response?.data?.detail || "We couldn't update that.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="preferences-page">
      <header className="w-full max-w-screen-md mx-auto px-6 h-16 flex items-center"><Logo /></header>
      <main className="max-w-lg mx-auto px-6 pt-8 page-fade">
        {error && !done && (
          <div data-testid="prefs-error">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: "#FDECEA", color: "#B04635" }}>
              <XCircle size={26} />
            </div>
            <h1 className="font-display text-3xl font-semibold" style={{ color: "var(--ta-primary)" }}>We couldn't open your preferences.</h1>
            <p className="mt-3 text-base" style={{ color: "var(--ta-text-secondary)" }}>{error}</p>
          </div>
        )}
        {!info && !error && <div className="text-sm" style={{ color: "var(--ta-text-secondary)" }}><Loader2 size={16} className="animate-spin inline mr-2" /> Loading…</div>}
        {info && !done && !error && (
          <>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>Monday Brief · Email preferences</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]" style={{ color: "var(--ta-primary)" }}>
              {info.businessName} — how should we email you?
            </h1>
            <p className="mt-3 text-base leading-relaxed" style={{ color: "var(--ta-text-secondary)" }}>
              You're currently <span className="font-semibold" style={{ color: "var(--ta-primary)" }}>{info.emailDeliveryStatus}</span> on <span className="font-mono">{info.email}</span>. Change your mind any time.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {info.emailDeliveryStatus === "active" && (
                <PrefBtn icon={PauseCircle} label="Pause my Monday Briefs" testId="prefs-pause" onClick={() => run("pause")} busy={busy} />
              )}
              {info.emailDeliveryStatus === "paused" && (
                <PrefBtn icon={PlayCircle} label="Resume my Monday Briefs" testId="prefs-resume" onClick={() => run("resume")} busy={busy} tone="primary" />
              )}
              {info.emailDeliveryStatus !== "unsubscribed" && (
                <PrefBtn icon={MailX} label="Unsubscribe from weekly briefs" testId="prefs-unsubscribe" onClick={() => run("unsubscribe")} busy={busy} tone="danger" />
              )}
              {info.emailDeliveryStatus === "unsubscribed" && (
                <div className="p-4 rounded-2xl text-sm" style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text)" }}>
                  You're unsubscribed. We won't email your Monday Brief anymore. If you change your mind, just say the word.
                </div>
              )}
              <PrefBtn icon={LifeBuoy} label="Something's wrong — I need help" testId="prefs-help" onClick={() => run("request_help")} busy={busy} tone="outline" />
            </div>
            <p className="mt-6 text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              This preferences link expires in a few weeks for your security. Need to change your email address? Reply to a Monday Brief and we'll help you verify.
            </p>
          </>
        )}
        {done && (
          <div data-testid="prefs-done">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4" style={{ background: "var(--ta-success-bg)", color: "var(--ta-success)" }}>
              <CheckCircle2 size={26} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" style={{ color: "var(--ta-primary)" }}>Got it.</h1>
            <p className="mt-3 text-base" style={{ color: "var(--ta-text-secondary)" }}>{done.message}</p>
            <Link to="/brief" className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-2xl text-base font-medium text-white" style={{ background: "var(--ta-primary)" }}>
              Open my full Monday Brief
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

const PrefBtn = ({ icon: Icon, label, onClick, busy, testId, tone = "outline" }) => {
  const styles = tone === "primary"
    ? { bg: "var(--ta-primary)", color: "#fff", border: "var(--ta-primary)" }
    : tone === "danger"
    ? { bg: "#fff", color: "#B04635", border: "#B04635" }
    : { bg: "#fff", color: "var(--ta-primary)", border: "var(--ta-border)" };
  return (
    <button onClick={onClick} disabled={busy} data-testid={testId}
      className="w-full inline-flex items-center gap-3 min-h-[64px] px-5 rounded-2xl text-base font-medium border-2 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
      style={{ background: styles.bg, color: styles.color, borderColor: styles.border }}>
      {busy ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} />} {label}
    </button>
  );
};
